import React, { useState } from "react";
import {
  FaBullhorn,
  FaCheckCircle,
  FaChevronDown,
  FaCode,
  FaHandshake,
  FaHeadset,
  FaUser,
  FaUserShield,
  FaUserTie,
} from "react-icons/fa";
import {
  ALL_USER_ROLES,
  getPermissionDisplayName,
  getRoleDisplayName,
  getServiceDomainLabel,
  MATRIX_GENERAL_PERMISSIONS,
  MATRIX_SERVICE_DOMAINS,
  roleHasMatrixPermission,
  serviceMatrixLevels,
  UserRole,
  type ServiceAccessLevel,
} from "src/lib/roles";

function roleColumnIcon(role: UserRole) {
  switch (role) {
    case UserRole.ADMIN:
      return <FaUserShield className="text-red-400" />;
    case UserRole.DEVELOPER:
      return <FaCode className="text-emerald-400" />;
    case UserRole.PRODUCT:
      return <FaUserTie className="text-blue-400" />;
    case UserRole.MARKETING:
      return <FaBullhorn className="text-pink-400" />;
    case UserRole.SALES:
      return <FaHandshake className="text-amber-400" />;
    case UserRole.CUSTOMER_SUPPORT:
      return <FaHeadset className="text-cyan-400" />;
    case UserRole.VIEWER:
      return <FaUser className="text-gray-400" />;
  }
}

const LEVEL_LABELS: Record<ServiceAccessLevel, string> = {
  read: "Read",
  write: "Write",
  manage: "Manage",
};

function ServiceAccessBadges({
  read,
  write,
  manage,
}: {
  read: boolean;
  write: boolean;
  manage: boolean;
}) {
  const levels: { key: ServiceAccessLevel; active: boolean }[] = [
    { key: "read", active: read },
    { key: "write", active: write },
    { key: "manage", active: manage },
  ];

  if (!read && !write && !manage) {
    return (
      <span className="text-gray-600" aria-hidden>
        —
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center justify-center gap-0.5"
      aria-label={levels
        .filter((l) => l.active)
        .map((l) => LEVEL_LABELS[l.key])
        .join(", ")}
    >
      {levels.map(({ key, active }) => (
        <span
          key={key}
          className={[
            "inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded px-1 text-[0.6rem] font-extrabold uppercase leading-none",
            active
              ? "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40"
              : "bg-white/5 text-gray-600 ring-1 ring-white/5",
          ].join(" ")}
          aria-hidden
        >
          {key[0]}
        </span>
      ))}
    </span>
  );
}

type RolePermissionsMatrixProps = {
  className?: string;
  /** When true, always show the table (no accordion toggle). */
  embedded?: boolean;
};

export const RolePermissionsMatrix: React.FC<RolePermissionsMatrixProps> = ({
  className = "mb-8",
  embedded = false,
}) => {
  const [open, setOpen] = useState(embedded);

  const table = (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[52rem] text-sm">
        <thead>
          <tr className="border-b border-white/10 bg-white/[0.03]">
            <th className="px-4 py-2.5 text-left font-medium text-gray-400 w-[28%] min-w-[10rem]">
              Permission
            </th>
            {ALL_USER_ROLES.map((role) => (
              <th
                key={role}
                className="px-2 py-2.5 text-center font-medium text-gray-300 min-w-[5.25rem]"
              >
                <span className="inline-flex flex-col items-center justify-center gap-1">
                  {roleColumnIcon(role)}
                  <span className="text-[0.65rem] leading-tight max-w-[5.5rem]">
                    {getRoleDisplayName(role)}
                  </span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {MATRIX_GENERAL_PERMISSIONS.map((perm) => (
            <tr key={perm} className="hover:bg-white/[0.02]">
              <td className="px-4 py-2 text-gray-300 whitespace-nowrap">
                {getPermissionDisplayName(perm)}
              </td>
              {ALL_USER_ROLES.map((role) => (
                <td key={`${perm}-${role}`} className="px-2 py-2 text-center">
                  {roleHasMatrixPermission(role, perm) ? (
                    <FaCheckCircle
                      className="inline-block text-emerald-400/90 text-xs"
                      aria-label="Yes"
                    />
                  ) : (
                    <span className="text-gray-600" aria-hidden>
                      —
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
          <tr className="bg-white/[0.04] border-t border-white/10">
            <td colSpan={1 + ALL_USER_ROLES.length} className="px-4 py-2">
              <span className="text-xs font-bold uppercase tracking-wide text-gray-400">
                Services
              </span>
              <span className="ml-2 text-xs font-normal text-gray-500">
                R = read · W = write · M = manage
              </span>
            </td>
          </tr>
          {MATRIX_SERVICE_DOMAINS.map((domain) => (
            <tr key={domain} className="hover:bg-white/[0.02]">
              <td className="px-4 py-2 text-gray-300 whitespace-nowrap">
                {getServiceDomainLabel(domain)}
              </td>
              {ALL_USER_ROLES.map((role) => (
                <td key={`${domain}-${role}`} className="px-2 py-2 text-center">
                  <ServiceAccessBadges {...serviceMatrixLevels(role, domain)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (embedded) {
    return (
      <div
        className={`${className} overflow-hidden rounded-[1.25rem] border-4 border-white/10 bg-gray-950/40 shadow-[0_6px_0_rgba(0,0,0,0.35)]`}
      >
        {table}
      </div>
    );
  }

  return (
    <div
      className={`${className} bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-white/[0.04] transition-colors ${
          open ? "border-b border-white/10" : ""
        }`}
      >
        <div>
          <h2 className="text-sm font-semibold text-gray-200">
            Who can access what
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Permissions by team · services show R / W / M
          </p>
        </div>
        <FaChevronDown
          className={`text-gray-400 shrink-0 text-sm transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
          aria-hidden
        />
      </button>
      {open && table}
    </div>
  );
};
